<template>
    <div>
        <div class="space-y-3" v-if="files.length">
            <div
                v-for="(file, index) in files"
                :key="file.id"
                class="relative cursor-pointer rounded-2xl border border-slate-100 bg-slate-50/70 p-4 shadow-sm transition-colors hover:border-cyan-200 hover:bg-cyan-50/40"
                @click="openFile(file)"
            >
                <div class="absolute left-6 top-0 hidden h-full w-px bg-slate-200 sm:block" v-if="timeline && index !== files.length - 1"></div>

                <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <div class="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-cyan-600 shadow-sm ring-1 ring-slate-200">
                        <span class="text-sm font-bold uppercase">{{ getExtension(file.description) }}</span>
                    </div>

                    <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                            <input
                                v-if="editingFileId === file.id"
                                v-model="editingFileName"
                                :ref="focusAndSelectInput"
                                class="w-full flex-1 rounded-md border border-cyan-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:ring focus:ring-cyan-400 focus:ring-opacity-25"
                                @click.stop
                                @keyup.enter="saveFileName(file)"
                                @keyup.esc="cancelFileNameEdit"
                                @blur="saveFileName(file)"
                            />
                            <div v-else class="truncate text-sm font-semibold text-slate-800">{{ file.exam_description || file.description }}</div>
                            <svg v-if="savingFileId === file.id" class="h-4 w-4 shrink-0 animate-spin text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <button
                                v-else-if="editingFileId !== file.id"
                                type="button"
                                class="shrink-0 text-slate-400 transition-colors hover:text-cyan-600"
                                title="Renomear arquivo"
                                @click.stop="startFileNameEdit(file)"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </button>
                        </div>
                        <div class="mt-1 text-xs text-slate-400">
                            {{ humanFileSize(file.size) }}<template v-if="file.created_at"> · {{ formatDateTime(file.created_at) }}</template>
                        </div>
                        <div v-if="file.exam_description && file.exam_description !== file.description" class="mt-1 text-xs text-slate-500 break-all">
                            Arquivo: {{ file.description }}
                        </div>
                    </div>

                    <div v-if="deleteControl" class="flex justify-end sm:block">
                        <BaseButton skin="danger-small" @click.stop="removeFile(file.id)">
                            <template v-slot:icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </template>
                        </BaseButton>
                    </div>
                </div>
            </div>

            <div class="flex justify-center" v-if="loading">
                <span class="text-cyan-500 p-4">
                    Atualizando arquivos...
                </span>
                <svg class="animate-spin mt-4 h-5 w-5 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import Swal from 'sweetalert';
import UploadService from '../services/FileService';

export default {
    name: 'FileTable',
    props: {
        files: Array,
        loading: Boolean,
        timeline: {
            type: Boolean,
            default: false
        },
        deleteControl: {
            type: Boolean,
            default: true
        }
    },
    setup(props, context) {
        const editingFileId = ref(null);
        const editingFileName = ref('');
        const savingFileId = ref(null);

        function getExtension(filename) {
            if (!filename || filename.indexOf('.') === -1) return 'file';
            return filename.split('.').pop().slice(0, 4);
        }

        function removeFile(fileId) {
            context.emit('remove-file', fileId);
        }

        function openFile(file) {
            if (editingFileId.value === file.id || !file.url) return;
            window.open(file.url, '_blank', 'noopener,noreferrer');
        }

        function humanFileSize(size) {
            const numericSize = Number(size);
            if (!numericSize) return '0 B';
            const i = Math.floor(Math.log(numericSize) / Math.log(1024));
            return `${(numericSize / Math.pow(1024, i)).toFixed(2) * 1} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        function startFileNameEdit(file) {
            editingFileId.value = file.id;
            editingFileName.value = file.exam_description || file.description || '';
        }

        function focusAndSelectInput(el) {
            if (!el || document.activeElement === el) return;
            el.focus();
            el.select();
        }

        function cancelFileNameEdit() {
            editingFileId.value = null;
            editingFileName.value = '';
        }

        async function saveFileName(file) {
            if (editingFileId.value !== file.id) return;

            const newName = editingFileName.value.trim();
            const previousName = file.exam_description || file.description || '';
            editingFileId.value = null;

            if (!newName || newName === previousName) return;

            savingFileId.value = file.id;

            try {
                await UploadService.updateExamDescription(file.id, newName);
                file.exam_description = newName;
            } catch (err) {
                Swal({
                    title: 'Erro ao renomear arquivo',
                    text: 'Tente novamente, caso o erro persista atualize a página.',
                    icon: 'error'
                });
            } finally {
                savingFileId.value = null;
            }
        }

        return {
            humanFileSize,
            formatDateTime,
            getExtension,
            removeFile,
            openFile,
            editingFileId,
            editingFileName,
            savingFileId,
            startFileNameEdit,
            focusAndSelectInput,
            cancelFileNameEdit,
            saveFileName
        }
    }
}
</script>
